import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ProfilePictureHoverProps {
  src?: string | null;
  name: string;
  size?: number;
  previewSize?: number;
  borderRadius?: number;
  cacheBust?: string;
  filter?: string;
}

const AVATAR_GRADIENT = 'linear-gradient(135deg, #4169E1, #5B7CE6)';

const Overlay: React.FC<{
  src: string;
  name: string;
  anchorRect: DOMRect;
  previewSize: number;
  onError: () => void;
  imgError: boolean;
  filter?: string;
}> = ({ src, name, anchorRect, previewSize, onError, imgError, filter }) => {
  const pad = 14;
  let left = anchorRect.right + pad;
  if (left + previewSize > window.innerWidth - 8) left = anchorRect.left - previewSize - pad;
  let top = anchorRect.top + anchorRect.height / 2 - previewSize / 2;
  top = Math.max(8, Math.min(top, window.innerHeight - previewSize - 8));

  return (
    <>
      <style>{`@keyframes phov-pop{from{opacity:0;transform:scale(.82)}to{opacity:1;transform:scale(1)}}`}</style>
      <div style={{
        position: 'fixed', top, left, zIndex: 9999,
        width: previewSize, height: previewSize,
        borderRadius: 18, overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,.22)',
        border: '3px solid rgba(255,255,255,.9)',
        pointerEvents: 'none',
        animation: 'phov-pop .18s cubic-bezier(.34,1.56,.64,1)',
        filter: filter || undefined,
      }}>
        {src && !imgError ? (
          <img src={src} alt={name} onError={onError}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{
            width: '100%', height: '100%', background: AVATAR_GRADIENT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: previewSize * 0.38, fontWeight: 700, color: 'white', fontFamily: 'system-ui,sans-serif',
          }}>
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
    </>
  );
};

const ProfilePictureHover: React.FC<ProfilePictureHoverProps> = ({
  src, name, size = 38, previewSize = 240, borderRadius = 10, cacheBust, filter,
}) => {
  const [hovered, setHovered]       = useState(false);
  const [imgError, setImgError]     = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const thumbRef                    = useRef<HTMLDivElement>(null);
  const timerRef                    = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setImgError(false); }, [src]);

  const resolvedSrc = src ? (cacheBust ? `${src}?v=${cacheBust}` : src) : '';

  const handleMouseEnter = useCallback(() => {
    timerRef.current = setTimeout(() => {
      if (thumbRef.current) {
        setAnchorRect(thumbRef.current.getBoundingClientRect());
        setHovered(true);
      }
    }, 100);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setHovered(false);
  }, []);

  useEffect(() => {
    if (!hovered) return;
    const hide = () => setHovered(false);
    window.addEventListener('scroll', hide, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', hide, { capture: true });
  }, [hovered]);

  const scaledStyle = hovered ? { transform: 'scale(1.08)', boxShadow: '0 4px 16px rgba(0,0,0,.18)' } : {};

  return (
    <>
      <div ref={thumbRef}
        style={{ flexShrink: 0, position: 'relative', cursor: 'zoom-in' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {resolvedSrc && !imgError ? (
          <img src={resolvedSrc} alt={name} onError={() => setImgError(true)}
            style={{
              width: size, height: size, borderRadius, objectFit: 'cover',
              border: '2px solid #f3f4f6', display: 'block',
              transition: 'transform .15s, box-shadow .15s',
              filter: filter || undefined, ...scaledStyle,
            }}
          />
        ) : (
          <div style={{
            width: size, height: size, borderRadius,
            background: AVATAR_GRADIENT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #f3f4f6',
            fontSize: size * 0.4, fontWeight: 600, color: 'white', fontFamily: 'system-ui,sans-serif',
            transition: 'transform .15s, box-shadow .15s',
            filter: filter || undefined, ...scaledStyle,
          }}>
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {hovered && anchorRect && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, pointerEvents: 'none' }}>
          <Overlay
            src={resolvedSrc} name={name} anchorRect={anchorRect}
            previewSize={previewSize} onError={() => setImgError(true)}
            imgError={imgError} filter={filter}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default ProfilePictureHover;